import classnames from 'classnames'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { User } from '../../../models/User'
import { userCanEditUserProfile } from '../../../state/calculations/permissions/userCan'
import useUpdateUserAvatar from '../../../state/mutations/useUpdateUserAvatar'
import { useSession } from '../../../state/session'
import useImageInput from '../../basics/ImageInput/useImageInput'
import UserAvatar from '../../UserAvatar/UserAvatar'
import Button from '../../basics/Button/Button'

import styles from './UserAvatarForm.module.scss'

export default React.memo(function UserAvatarForm({ user, className }: { user: User; className?: string }) {
  const { t } = useTranslation()
  const { user: currentUser } = useSession()

  const [value, setValue] = useState(user.avatarUrl)
  useEffect(() => {
    setValue(user.avatarUrl)
  }, [setValue, user.avatarUrl])

  const imageInput = useImageInput({ value, setValue, initialValue: user.avatarUrl, aspect: 1, circularCrop: true })

  const updateUserAvatar = useUpdateUserAvatar()
  const handleSave = useCallback(async () => {
    await updateUserAvatar.mutateAsync({ uuid: user.uuid, imageData: value! })
  }, [user, value])

  if (!userCanEditUserProfile(currentUser, user)) {
    return (
      <section className={classnames(styles.UserAvatarForm, className)}>
        <div>
          <UserAvatar user={{ ...user, avatarUrl: value }} large className={styles.avatar} />
        </div>
      </section>
    )
  }

  return (
    <section className={classnames(styles.UserAvatarForm, className)}>
      <div {...imageInput.rootProps}>
        <UserAvatar user={{ ...user, avatarUrl: value }} large className={styles.avatar} />
        <input {...imageInput.inputProps} />
      </div>
      <div className={styles.actions}>
        {!imageInput.discardButtonProps.disabled && (
          <Button small {...imageInput.discardButtonProps}>
            {t('generic cta.discard')}
          </Button>
        )}
        {!imageInput.removeButtonProps.disabled && (
          <Button small {...imageInput.removeButtonProps}>
            {t('generic cta.remove')}
          </Button>
        )}
        {!imageInput.adjustButtonProps.disabled && (
          <Button small filled {...imageInput.adjustButtonProps}>
            {t('cta crop image')}
          </Button>
        )}
        {value === user.avatarUrl && (
          <Button small filled {...imageInput.selectButtonProps}>
            {t('generic cta.upload')}
          </Button>
        )}
        {value !== user.avatarUrl && (
          <Button small filled onClick={handleSave}>
            {t('generic cta.save')}
          </Button>
        )}
      </div>
      {imageInput.children}
    </section>
  )
})
